// src/components/BankVerificationAllocatedToAssociate.js
import React, { useState, useEffect } from "react";
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Tabs, Tab, IconButton, Button,
} from "@mui/material";
import { Sync, ArrowBack } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import apiClient from "./api/apiClient";
import moment from "moment";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const BankVerificationAllocatedToAssociate = () => {
  const [yetToSend, setYetToSend] = useState([]);
  const [requested, setRequested] = useState([]);
  const [scheduled, setScheduled] = useState([]);
  const [completed, setCompleted] = useState([]);
  const [tabIndex, setTabIndex] = useState(0);

  // cache: schedulerId -> [dates]; meetingId -> scheduledAt
  const [proposedDatesBySchedulerId, setProposedDatesBySchedulerId] = useState({});
  const [scheduledAtByMeetingId, setScheduledAtByMeetingId] = useState({});

  const [tatSummary, setTatSummary] = useState({
    yetToSend: { within: 0, beyond: 0 },
    requested: { within: 0, beyond: 0 },
    scheduled: { within: 0, beyond: 0 },
    completed: { within: 0, beyond: 0 },
  });
  

  const navigate = useNavigate();
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    if (userId) fetchBankVerifications(userId);
  }, [userId]);

  const fetchBankVerifications = async () => {
    try {
      const res = await apiClient.get(`/bankverifications`);
      const y = [], r = [], s = [],c=[];
      res.data.forEach((item) => {
        if (String(item.allocatedToAssociate) === String(userId)) {
          const status = item.status?.trim().toUpperCase();
          if (!status || status === "ALLOCATED") y.push(item);
          else if (status === "REQUESTED") r.push(item);
          else if (status === "SCHEDULED") s.push(item);
          else if (status === "COMPLETED" || (status !== "REQUESTED" && status !== "SCHEDULED" )) c.push(item); 
        }
  
      });
      const tatStats = {
        yetToSend: calcTAT(y,"YET-TO-ALLOCATE"),
        requested: calcTAT(r,"REQUESTED"),
        scheduled: calcTAT(s,"SCHEDULED"),
        completed: calcTAT(c,"COMPLETED"),
      };
      setYetToSend(y); setRequested(r); setScheduled(s);setCompleted(c);setTatSummary(tatStats);
    } catch (err) {
      console.error("Error fetching bank verifications", err);
    }
  };
  const calcTAT = (list,type) => {
    const today = moment();
    let within = 0, beyond = 0;
    list.forEach(item => {
      if(type === "COMPLETED"){
        if(item.dateOfCompletion){
          const days = today.diff(moment(item.dateOfCompletion), 'days');
          if (days <= 3) within++;
          else beyond++;
        }else{
          within++;
        }
      }else if (item.dateOfAllocationToAssociate) {
        const days = today.diff(moment(item.dateOfAllocationToAssociate), "days");
        if (days <= 3) within++;
        else beyond++;
      } else {
        beyond++;
      }
    });
    return { within, beyond };
  };
  // preload proposed dates for REQUESTED tab
  useEffect(() => {
    const load = async () => {
      const ids = [...new Set(requested.map(i => i.latestMeetingSchedulerId).filter(Boolean))];
      if (!ids.length) return;
      try {
        const entries = await Promise.all(
          ids.map(async (id) => {
            const res = await apiClient.get(`/meetingschedulers/${id}`);
            return [id, res.data?.proposedDates || []];
          })
        );
        setProposedDatesBySchedulerId(prev => ({ ...prev, ...Object.fromEntries(entries) }));
      } catch (e) {
        console.error("Failed to load proposed dates for requested", e);
      }
    };
    load();
  }, [requested]);

  // preload proposed dates & scheduled date for SCHEDULED tab
  useEffect(() => {
    const load = async () => {
      const schedulerIds = [...new Set(scheduled.map(i => i.latestMeetingSchedulerId).filter(Boolean))];
      const meetingIds = [...new Set(scheduled.map(i => i.latestMeetingId).filter(Boolean))];
      try {
        if (schedulerIds.length) {
          const schedEntries = await Promise.all(
            schedulerIds.map(async (id) => {
              const res = await apiClient.get(`/meetingschedulers/${id}`);
              return [id, res.data?.proposedDates || []];
            })
          );
          setProposedDatesBySchedulerId(prev => ({ ...prev, ...Object.fromEntries(schedEntries) }));
        }
        if (meetingIds.length) {
          const meetEntries = await Promise.all(
            meetingIds.map(async (id) => {
              const res = await apiClient.get(`/meetings/${id}`);
              return [id, res.data?.scheduledAt || null];
            })
          );
          setScheduledAtByMeetingId(prev => ({ ...prev, ...Object.fromEntries(meetEntries) }));
        }
      } catch (e) {
        console.error("Failed to load data for scheduled", e);
      }
    };
    load();
  }, [scheduled]);

  const handleTabChange = (_e, newVal) => setTabIndex(newVal);
  const handleBack = () => navigate(-1);

  const handleJoin = async (meetingId,caseId) => {
    try {
      const response = await apiClient.get(`/meetings/${meetingId}`);
//      navigate(`/meetingroom/${response.data.roomId}/${meetingId}/${caseId}`, { state: { isInitiator: true } });
      navigate(`/meetingroom/${response.data.roomId}/${meetingId}`, { state: { isInitiator: true ,caseId:caseId}});
    } catch (error) {
      console.error("Error reading meeting", error);
    }
  };

  const handleDetails = (caseId)=>{
     navigate(`/bankverificationcasedetails/${caseId}`)
  }


  const handleSendRequest = (item) => {
    // For creating or editing, navigate accordingly
    if (item.status?.toUpperCase() === "REQUESTED" && item.latestMeetingSchedulerId) {
      // edit existing MeetingScheduler
      navigate(`/createmeetingrequest/${item.latestMeetingSchedulerId}`);
    } else {
      // create new
      navigate("/createmeetingrequest", {
        state: {
          title: "Bank Statement Verification",
          inviteeName: item.candidateName,
          inviteeEmail: item.email,
          inviteeMobilePhoneNumber: item.mobilePhoneNumber,
          bankVerificationId: item.id
        }
      });
    }
  };
  const handleExportToExcel = () => {
    let dataToExport = [];
    let tabName = "";
  
    if (tabIndex === 0) {
      dataToExport = yetToSend;
      tabName = "YetToSend";
    } else if (tabIndex === 1) {
      dataToExport = requested;
      tabName = "Requested";
    } else if (tabIndex === 2) {
      dataToExport = scheduled;
      tabName = "Scheduled";
    } else if (tabIndex === 3) {
      dataToExport = completed;
      tabName = "Completed";
    }
  
    if (!dataToExport.length) {
      alert("No data to export for this tab.");
      return;
    }
  
    // Format the data (remove nested objects)
    const formattedData = dataToExport.map(item => ({
      "VB Number": item.caseId,
      "Check ID": item.checkId,
      "Client Name": item.clientName,
      "Candidate Name": item.candidateName,
      "Emp Type": item.employmentType,
      "Company Name": item.companyName,
      "Mobile Phone Number": item.mobilePhoneNumber,
      "Email": item.email,
      "Tenure From": item.tenureFrom ? moment(item.tenureFrom).format("DD-MMM-YYYY") : "",
      "Tenure To": item.tenureTo ? moment(item.tenureTo).format("DD-MMM-YYYY") : "",
      "Assigned By Emp Exec": item.assignedByEmpExec,
      "BS Init. Date": item.bsInitiationDate ? moment(item.bsInitiationDate).format("DD-MMM-YYYY") : "",
      "Status": item.status,
      "Allocation Date": item.dateOfAllocationToAssociate
        ? moment(item.dateOfAllocationToAssociate).format("DD-MMM-YYYY")
        : ""
    }));
  
    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, tabName);
  
    const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
    const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  
    saveAs(blob, `BankVerification_${tabName}_${moment().format("YYYYMMDD_HHmm")}.xlsx`);
  };
  

  const renderTable = (data) => {
    const hasRequested = data.some(d => (d.status || "").toUpperCase() === "REQUESTED");
    const hasScheduled = data.some(d => (d.status || "").toUpperCase() === "SCHEDULED");
    const hasCompleted = data.some(d => (d.status || "").toUpperCase() === "COMPLETED");

    return (
      <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell>VB Number</TableCell>
              <TableCell>Check ID</TableCell>
              <TableCell>Client Name</TableCell>
              <TableCell>Candidate Name</TableCell>
              <TableCell>Emp Type</TableCell>
              <TableCell>Company Name</TableCell>
              <TableCell>Mobile Phone Number</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Tenure From</TableCell>
              <TableCell>Tenure To</TableCell>
              <TableCell>Assigned By Emp Exec</TableCell>
              <TableCell>BS Init. Date</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Allocation Date</TableCell>
              {hasRequested && <TableCell>Proposed Dates</TableCell>}
              {(hasScheduled && hasCompleted) && (
                <>
                  <TableCell>Proposed Dates</TableCell>
                  <TableCell>Scheduled Date</TableCell>
                </>
              )}
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.map((item) => {
              const status = (item.status || "").toUpperCase();
              const schedulerId = item.latestMeetingSchedulerId;
              const meetingId = item.latestMeetingId;

              const proposed = schedulerId
                ? proposedDatesBySchedulerId[schedulerId] || []
                : [];

              const scheduledAt = meetingId
                ? scheduledAtByMeetingId[meetingId]
                : null;

              return (
                <TableRow key={item.id} hover>
                  <TableCell>{item.caseId}</TableCell>
                  <TableCell>{item.checkId}</TableCell>
                  <TableCell>{item.clientName}</TableCell>
                  <TableCell>{item.candidateName}</TableCell>
                  <TableCell>{item.employmentType}</TableCell>
                  <TableCell>{item.companyName}</TableCell>
                  <TableCell>{item.mobilePhoneNumber}</TableCell>
                  <TableCell>{item.email}</TableCell>
                  <TableCell>
                    {item.tenureFrom ? moment(item.tenureFrom).format("DD-MMM-YYYY") : ""}
                  </TableCell>
                  <TableCell>
                    {item.tenureTo ? moment(item.tenureTo).format("DD-MMM-YYYY") : ""}
                  </TableCell>
                  <TableCell>{item.assignedByEmpExec}</TableCell>
                  <TableCell>
                    {item.bsInitiationDate ? moment(item.bsInitiationDate).format("DD-MMM-YYYY") : ""}
                  </TableCell>                  
                  <TableCell>{status}</TableCell>
                  <TableCell>
                    {item.dateOfAllocationToAssociate
                      ? moment(item.dateOfAllocationToAssociate).format("DD-MMM-YYYY")
                      : ""}
                  </TableCell>

                  {status === "REQUESTED" && (
                    <TableCell>
                      {proposed.length
                        ? proposed.map((d, i) => (
                            <div key={i}>{moment(d).format("DD-MMM-YYYY HH:mm")}</div>
                          ))
                        : <em>—</em>}
                    </TableCell>
                  )}

                  {status === "SCHEDULED" && (
                    <>
                      <TableCell>
                        {proposed.length
                          ? proposed.map((d, i) => (
                              <div key={i}>{moment(d).format("DD-MMM-YYYY HH:mm")}</div>
                            ))
                          : <em>—</em>}
                      </TableCell>
                      <TableCell>
                        {scheduledAt ? moment(scheduledAt).format("DD-MMM-YYYY HH:mm") : <em>—</em>}
                      </TableCell>
                    </>
                  )}

                  <TableCell>
                    {status === "ALLOCATED" && (
                      <Button
                        variant="contained"
                        color="primary"
                        size="small"
                        onClick={() => handleSendRequest(item)}
                      >
                        Send Meeting Request
                      </Button>
                    )}

                    {status === "SCHEDULED" && (
                      <>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={() => handleJoin(meetingId,item.id)}
                        >
                          Join Meeting
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleDetails(item.id)}
                        >
                          Details
                        </Button>
                      </>  
                    )}
                    {/* ✅ If COMPLETED → show only Details */}
                    {(status === "COMPLETED" || status === "STOP-CHECK" || status === "UNABLE-TO-VERIFY" || status === "FOLLOWUP-MAIL-1-SENT" || status === "FOLLOWUP-MAIL-2-SENT" || status === "FOLLOWUP-MAIL-3-SENT" || status === "INITIATED-TO-PV" || status === "RESCHEDULED") && (
                        <Button
                          variant="contained"
                          color="primary"
                          size="small"
                          onClick={() => handleDetails(item.id)}
                        >
                          Details
                        </Button>
                      )}
                    {/* No button for ALLOCATED */}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ width: "95%", margin: "auto", mt: 4 }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => navigate(-1)}><ArrowBack /></IconButton>
          <Typography variant="h5" sx={{ fontWeight: "bold", ml: 1 }}>
            Bank Verifications Allocated to Me
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={handleExportToExcel}
          >
            Export to Excel
          </Button>
          <IconButton onClick={fetchBankVerifications}><Sync /></IconButton>
      </Box>
      </Box>

      <Box sx={{ mb: 3 }}>
  <Typography variant="h6" sx={{ mb: 1, fontWeight: "bold" }}>
    Bank Verification Progress (TAT-sensitive)
  </Typography>

  {(() => {
    const total = yetToSend.length + requested.length + scheduled.length + completed.length;
    const pct = (count) => (total ? (count / total) * 100 : 0);

    const segmentStyle = (color, width) => ({
      width: `${width}%`,
      backgroundColor: color,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontWeight: "bold",
      fontSize: 13,
      transition: "width 0.8s ease-in-out",
    });

    // helper to render TAT mini-bars
    const renderTat = (label, tat, totalBucket, colorMain) => {
      const totalTat = tat.within + tat.beyond;
      if (!totalTat) return null;

      const withinPct = (tat.within / total) * 100;
      const beyondPct = (tat.beyond / total) * 100;

      return (
        <>
          <Box sx={{ ...segmentStyle(colorMain, withinPct) }}>
            {tat.within > 0 && `${tat.within} ${label}✓`}
          </Box>
          <Box sx={{ ...segmentStyle("#ef5350", beyondPct) }}>
            {tat.beyond > 0 && `${tat.beyond} ${label}✗`}
          </Box>
        </>
      );
    };

    return (
      <Box
        sx={{
          display: "flex",
          height: 30,
          borderRadius: "20px",
          overflow: "hidden",
          boxShadow: 1,
          backgroundColor: "#f0f0f0",
        }}
      >
        {renderTat("Yet", tatSummary.yetToSend, yetToSend.length, "#fbc02d")}
        {renderTat("Req", tatSummary.requested, requested.length, "#42a5f5")}
        {renderTat("Sch", tatSummary.scheduled, scheduled.length, "#66bb6a")}
        {renderTat("Comp", tatSummary.completed, completed.length, "#ab47bc")}
      </Box>
    );
  })()}

  <Typography
    variant="body2"
    align="right"
    sx={{ mt: 0.5, fontStyle: "italic", color: "gray" }}
  >
    Total Cases: {yetToSend.length + requested.length + scheduled.length + completed.length}
  </Typography>
</Box>



      <Tabs value={tabIndex} onChange={(_e, v) => setTabIndex(v)} sx={{ mb: 2 }}>
        <Tab label={`Yet to Send Request${yetToSend.length ? ` (${yetToSend.length})` : ""}`} />
        <Tab label={`Requested${requested.length ? ` (${requested.length})` : ""}`} />
        <Tab label={`Scheduled${scheduled.length ? ` (${scheduled.length})` : ""}`} />
        <Tab label={`Completed${completed.length ? ` (${completed.length})` : ""}`} />
      </Tabs>

      {tabIndex === 0 && renderTable(yetToSend)}
      {tabIndex === 1 && renderTable(requested)}
      {tabIndex === 2 && renderTable(scheduled)}
      {tabIndex === 3 && renderTable(completed)}
    </Box>
  );
};

export default BankVerificationAllocatedToAssociate;
